import { useReducer, useEffect, useCallback } from 'react';
import localforage from 'localforage';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import OverviewInfoCard from '@/components/overview/OverviewInfoCard';
import { useAppContext } from '@/provider/contexts';
import {
    ApOverviewResponse,
    DashboardCards,
    DashboardPageAction,
    JobOverviewResponse,
    OVERVIEW_TYPE,
    OverviewMap,
    TagOverviewResponse,
} from '@/types/pages/dashboard';
import { fetchApOverview, fetchJobOverview, fetchTagOverview } from '@/api/dashboard';
import { Dashboard, ConnectedTv, PendingActions, CellTower } from '@mui/icons-material';
import '@/styles/pages/Dashboard/DashboardPage.scss';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const OVERVIEW_COUNT_EXTRACTOR = {
    [OVERVIEW_TYPE.AP]: (info: ApOverviewResponse) => ({
        online: info.online,
        max: info.max_aps,
    }),
    [OVERVIEW_TYPE.TAG]: (info: TagOverviewResponse) => ({
        online: info.online_tags,
        max: info.max_tags,
    }),
    [OVERVIEW_TYPE.JOB]: (info: JobOverviewResponse) => ({
        online: info.success_jobs,
        max: info.max_jobs,
    }),
} as const;

const deriveOverviewState = (
    target: OVERVIEW_TYPE,
    info: ApOverviewResponse | TagOverviewResponse | JobOverviewResponse,
) => {
    const { online, max } = OVERVIEW_COUNT_EXTRACTOR[target](info as any);

    if (max === 0) {
        return {
            ratioNum: ['0', '00'],
            online,
            max,
        };
    }

    const ratio = ((online / max) * 100).toFixed(2);
    return {
        ratioNum: ratio.split('.'),
        online,
        max,
    };
};

const reducer = (state: DashboardCards, action: DashboardPageAction) => {
    switch (action.type) {
        case 'SET_OVERVIEW_STATE': {
            const { target, hasData, info } = action.payload;
            if (hasData) {
                const overviewState = deriveOverviewState(
                    target,
                    info as ApOverviewResponse | TagOverviewResponse | JobOverviewResponse,
                );
                return {
                    ...state,
                    [target]: {
                        ...state[target],
                        hasData,
                        ...overviewState,
                    },
                };
            }
            return {
                ...state,
                [target]: {
                    ...state[target],
                    ...action.payload,
                },
            };
        }
        default:
            return state;
    }
};

const OVER_VIEW_API: OverviewMap = {
    [OVERVIEW_TYPE.AP]: fetchApOverview,
    [OVERVIEW_TYPE.TAG]: fetchTagOverview,
    [OVERVIEW_TYPE.JOB]: fetchJobOverview,
};

const DashboardPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const defaultState: DashboardCards = {
        [OVERVIEW_TYPE.AP]: {
            loading: true,
            hasData: false,
            online: 0,
            max: 0,
            ratioNum: ['0', '00'],
        },
        [OVERVIEW_TYPE.TAG]: {
            loading: true,
            hasData: false,
            online: 0,
            max: 0,
            ratioNum: ['0', '00'],
        },
        [OVERVIEW_TYPE.JOB]: {
            loading: true,
            hasData: false,
            online: 0,
            max: 0,
            ratioNum: ['0', '00'],
        },
    };

    const [state, dispatch] = useReducer(reducer, defaultState);

    const { userDetails, setNotification } = useAppContext();

    const hasPermission = !!userDetails && userDetails.user_level >= 2;

    useEffect(() => {
        const initialize = async () => {
            const corporationId = await localforage.getItem<number>('corporation_id');
            if (!corporationId) return;

            fetchOverviewByType(OVERVIEW_TYPE.AP, corporationId);
            fetchOverviewByType(OVERVIEW_TYPE.TAG, corporationId);
            fetchOverviewByType(OVERVIEW_TYPE.JOB, corporationId);
        };

        initialize();
    }, []);

    const fetchOverviewByType = async (type: OVERVIEW_TYPE, corporationId: number) => {
        const fetchOverViewAPI = OVER_VIEW_API[type];
        dispatch({
            type: 'SET_OVERVIEW_STATE',
            payload: {
                target: type,
                loading: true,
            },
        });
        try {
            const {
                data: { data, message, isSuccess },
            } = await fetchOverViewAPI(corporationId);
            if (isSuccess) {
                dispatch({
                    type: 'SET_OVERVIEW_STATE',
                    payload: {
                        target: type,
                        hasData: true,
                        info: data,
                    },
                });
                return;
            }
            throw new Error(message);
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Fetch Overview'), severity: 'error' });
                return;
            }
            console.error('Error saving message:', error);
        } finally {
            dispatch({
                type: 'SET_OVERVIEW_STATE',
                payload: {
                    target: type,
                    loading: false,
                },
            });
        }
    };

    const handleApCardClick = useCallback(() => {
        if (hasPermission) navigate('/devices', { state: { from: 'ap-ratio' } });
    }, [hasPermission, navigate]);

    const handleTagCardClick = useCallback(() => {
        if (hasPermission) navigate('/devices', { state: { from: 'device-ratio' } });
    }, [hasPermission, navigate]);

    const handleJobCardClick = useCallback(() => {
        if (hasPermission) navigate('/devices', { state: { from: 'job-ratio' } });
    }, [hasPermission, navigate]);

    return (
        <>
            <BasePaper className="overview-section" isFullHeight={true}>
                <BaseTitle icon={<Dashboard />} title={t('Dashboard')} />

                <div className="overview-cards-wrapper">
                    <OverviewInfoCard
                        info={state[OVERVIEW_TYPE.TAG]}
                        title={t('Device Ratio')}
                        icon={<ConnectedTv />}
                        isClickable={hasPermission}
                        onClick={handleTagCardClick}
                    />
                    <OverviewInfoCard
                        info={state[OVERVIEW_TYPE.JOB]}
                        title={t('Job Status Ratio')}
                        icon={<PendingActions />}
                        isClickable={hasPermission}
                        onClick={handleJobCardClick}
                    />
                    <OverviewInfoCard
                        info={state[OVERVIEW_TYPE.AP]}
                        title={t('AP Ratio')}
                        icon={<CellTower />}
                        isClickable={hasPermission}
                        onClick={handleApCardClick}
                    />
                </div>
            </BasePaper>
        </>
    );
};

export default DashboardPage;
